/* eslint-disable default-case,no-fallthrough */
// modules
import * as R from 'ramda'
import persist from '@weblite/persist'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
// import { envMode } from '$config/env'
import { GetHalls, reqGetHalls } from '@weblite/ts-action/hall'
import {
  GetSubscriptions,
  reqGetSubscriptions,
} from '@weblite/ts-action/subscription'
import { GetPvs, reqGetPvs } from '@weblite/ts-action/pv'
import { GetWappRooms, reqGetWappRooms } from '@weblite/ts-action/wapp-room'
// setup
import reduxStore from './redux'
// import { storyBookMockStore } from '../../.storybook/preview'
// hooks
import afterLocalRehydrate from '../hooks/after/localStorageRehydrate'
import afterReduxRehydrate from '../hooks/after/reduxRehydrate'
import { request, wsReq } from './ws'
import { beforeReduxRehydrate } from '$hooks/before/reduxRehydrate'
import { persistUpdateUserRequest } from '$logic/users/service'
import { getWappsFromServer } from '$logic/wapps/service'

// TODO: mv /types
// TODO: restructure Persist schema for every layer
// TODO: convert dest to action creator
interface Hooks {
  /** Reformat/transform server given data to wanted format before insert into layer */
  beforeInsert?: (args: any[]) => readonly any[] | Promise<readonly any[]>
}

interface StoreOptions {
  /** Path to store data in indexed-db */
  keyPath?: string | string[] | null // = 'id'
  /** Id works like the SQL db incremental ids */
  autoIncrement?: boolean // = false
}
interface PersistSchema {
  storeOpts?: StoreOptions
  /** Store option layer: indexed-db, redux, local-storage, session-storage */
  layer: 'db' | 'redux' | 'local' | 'session'
  /** Load data into redux in first load. Defaults to `false` */
  autoRehydrate?: boolean
  /** Composite indexes */
  indexes?: {
    keyPath: string | string[]
    name?: string //  keyPath.toString()
    unique?: boolean
    multiEntry?: boolean
  }[]
  /** Path to store data in redux. `null` if redux not needed */
  path?: string[]
  /** Normalization key. Defaults to `id` */
  reduxKey?: string // = 'id'
  /** action destination */
  dest?: string
  hooks?: Hooks

  updateOptions?: {
    // The key to determine if it's up to date. Defaults to `updatedAt`
    updateKey?: string

    // TODO: fix types from ts-action/utils/response ReturnType^2<actionMaker>
    request: (payload: { [key: string]: number | null }) => Promise<any[]>
  }
}

// Note: key of `PersistObject` must equal last element of `PersistSchema.path`
type PersistObject = Record<string, PersistSchema>
const completePersistObj: PersistObject = {
  // new
  messages: {
    layer: 'db',
    storeOpts: { keyPath: ['roomId', 'iid'] },
    path: ['main', 'messages'],
  },
  pendingMessages: {
    layer: 'db',
    autoRehydrate: true,
    path: ['main', 'pendingMessages'],
  },
  cachedFiles: {
    layer: 'db',
    storeOpts: { keyPath: 'url' },
    path: ['main', 'cachedFiles'],
  },
  auth: {
    layer: 'local',
    autoRehydrate: true,
    path: ['main', 'auth'],
  },
  wapps: {
    layer: 'db',
    autoRehydrate: true,
    indexes: [{ keyPath: 'name' }],
    path: ['main', 'wapps'],
    updateOptions: { request: getWappsFromServer },
  },
  // wappStats: {
  //   layer: 'db',
  //   autoRehydrate: true,
  //   path: ['main', 'wappStats'],
  // },
  wises: {
    layer: 'db',
    path: ['main', 'wises'],
    dest: 'wis',
  },
  contacts: {
    layer: 'db',
    path: ['main', 'contacts'],
    storeOpts: { keyPath: 'id', autoIncrement: true },
    indexes: [{ name: 'phone', keyPath: 'phone' }],
  },
  mockup: {
    layer: 'local',
    autoRehydrate: true,
    path: ['main', 'mockup'],
  },
  pvSync: { layer: 'db' },
  clientUserSync: { layer: 'db' },
  userPreferencesSync: { layer: 'db' },
  privateSpaceSync: { layer: 'db' },
  deletedRoomsSync: { layer: 'db' },
  wappRoomSync: { layer: 'db' },
  subscriptionSync: { layer: 'db' },
  draftSync: {
    layer: 'db',
    storeOpts: { keyPath: 'roomId' },
  },
  seenSync: {
    layer: 'db',
    storeOpts: { keyPath: 'roomId' },
  },
  seenMentionSync: { layer: 'db', storeOpts: { keyPath: 'roomId' } },
  keyval: {
    layer: 'db',
    path: ['main', 'keyval'],
    autoRehydrate: true,
  },
  rooms: {
    layer: 'db',
    path: ['main', 'rooms'],
    autoRehydrate: true,
    //   TODO: Maybe we should save room.lastMessage in message collection
  },
  halls: {
    layer: 'db',
    storeOpts: { keyPath: 'roomId' },
    indexes: [{ keyPath: 'identifier' }],
    path: ['main', 'halls'],
    reduxKey: 'roomId',
    updateOptions: {
      request: async halls => {
        const res = await request<GetHalls>(reqGetHalls({ halls }), {
          retry: -1,
        })
        return res?.payload?.halls ?? []
      },
    },
  },
  pvs: {
    layer: 'db',
    storeOpts: { keyPath: 'roomId' },
    indexes: [{ keyPath: 'to' }],
    path: ['main', 'pvs'],
    reduxKey: 'roomId',
    updateOptions: {
      request: async pvs => {
        const res = await request<GetPvs>(reqGetPvs({ pvs }), {
          retry: -1,
        })
        return res?.payload?.pvs ?? []
      },
    },
  },
  drafts: {
    layer: 'db',
    storeOpts: { keyPath: 'roomId' },
    path: ['main', 'drafts'],
    reduxKey: 'roomId',
    autoRehydrate: true,
  },
  subscriptions: {
    layer: 'db',
    storeOpts: { keyPath: 'hallId' },
    path: ['main', 'subscriptions'],
    reduxKey: 'hallId',
    updateOptions: {
      request: async subscriptions => {
        const res = await request<GetSubscriptions>(
          reqGetSubscriptions({ subscriptions }),
          {
            retry: -1,
          },
        )
        return res?.payload?.subscriptions ?? []
      },
    },
  },
  users: {
    layer: 'db',
    path: ['main', 'users'],
    indexes: [{ keyPath: 'username' }],
    updateOptions: { request: persistUpdateUserRequest },
  },
  wappRooms: {
    layer: 'db',
    storeOpts: { keyPath: 'roomId' },
    path: ['main', 'wappRooms'],
    reduxKey: 'roomId',
    updateOptions: {
      request: async wappRooms => {
        const res = await request<GetWappRooms>(
          reqGetWappRooms({ wappRooms }),
          {
            retry: -1,
          },
        )
        return res?.payload?.wappRooms ?? []
      },
    },
  },
  usersStatus: {
    layer: 'redux',
    path: ['main', 'usersStatus'],
  },
  openedRoomMessages: {
    layer: 'redux',
    path: ['main', 'openedRoomMessages'],
  },
  sharedMedia: {
    layer: 'db',
    storeOpts: { keyPath: ['roomId', 'category'] },
  },
  openedRoomSharedMedia: {
    layer: 'redux',
    path: ['main', 'openedRoomSharedMedia'],
  },
  hallInviteLinks: {
    layer: 'redux',
    path: ['main', 'hallInviteLinks'],
  },
  premiumFeaturesTrials: {
    // TODO: Remove this later
    layer: 'redux',
    path: ['main', 'premiumFeaturesTrials'],
    reduxKey: 'roomId',
  },
}

const persist1: PersistObject = {
  roomLastMessage: {
    layer: 'db',
    storeOpts: { keyPath: 'roomId' },
    path: ['main', 'roomLastMessage'],
    reduxKey: 'roomId',
    autoRehydrate: true,
  },
}

const persist2: PersistObject = {
  roomLastMessageSync: {
    layer: 'db',
    storeOpts: { keyPath: 'roomId' },
  },
}

const persist3: PersistObject = {
  uploadingFiles: {
    layer: 'db',
    path: ['main', 'uploadingFiles'],
  },
  pendingUploads: {
    layer: 'db',
    autoRehydrate: true,
    path: ['main', 'pendingUploads'],
  },
}

const persist4: PersistObject = {
  pinnedMessages: {
    layer: 'db',
    storeOpts: { keyPath: ['roomId'] },
  },
  openedRoomPinnedMessages: {
    layer: 'redux',
    path: ['main', 'openedRoomPinnedMessages'],
  },
}

const persist5: PersistObject = {
  pendingEdits: {
    layer: 'db',
    autoRehydrate: true,
    path: ['main', 'pendingEdits'],
  },
  pendingEditUploads: {
    layer: 'db',
    autoRehydrate: true,
    path: ['main', 'pendingEditUploads'],
  },
}

const persist6 = {
  roomLastScroll: {
    layer: 'db',
    storeOpts: { keyPath: 'roomId' },
  },
}

const persist7 = {
  workspace: {
    layer: 'db',
    storeOpts: { keyPath: 'id' },
    reduxKey: 'id',
    path: ['main', 'workspace'],
    autoRehydrate: true,
  },
}

const persist8 = {
  analyticsSync: {
    layer: 'db',
  },
}

// functions can't be stored in localStorage
type Persist0 = Record<string, Omit<PersistSchema, 'hooks'>>
const persist0: Persist0 = R.map(R.omit(['hooks']), completePersistObj)
// TODO get normalization key as option

/**
 * hooks are functions which accept array of documents as input and should
 * return an array of modified documents with the same size or
 * a promise that resolves to that array
 */
type RPluckOverride = (
  key: string,
) => (obj: Record<string, any>) => Record<string, any>

const hooks: Record<string, Hooks> = R.compose(
  R.filter(Boolean),
  (R.pluck as RPluckOverride)('hooks'),
)(completePersistObj)

const storeCreator = (db: any) =>
  R.pipe(
    R.filter(R.propEq('layer', 'db')),
    R.forEachObjIndexed(({ storeOpts = {}, indexes = [] }, storeName) => {
      const { keyPath = 'id', autoIncrement }: StoreOptions = storeOpts
      const store = db.createObjectStore(storeName, {
        keyPath,
        autoIncrement,
      })
      R.forEach(({ keyPath: indexKey, name, ...indexOpts }) => {
        store.createIndex(name || indexKey.toString(), indexKey, indexOpts)
      }, indexes)
    }),
  )

// TODO: db is IDBPDatabase<DBTypes>
const upgrade = (db: any, oldVersion: number, newVersion: number) => {
  // eslint-disable-next-line no-console
  console.log('idb upgrade needed:', oldVersion, '->', newVersion)

  const createStores = storeCreator(db)
  switch (oldVersion) {
    case 0:
      createStores(persist0)
    case 1:
      createStores(persist1)
    case 2:
      createStores(persist2)
    case 3:
      createStores(persist3)
    case 4:
      createStores(persist4)
    case 5:
      createStores(persist5)
    case 6:
      createStores(persist6)
    case 7:
      createStores(persist7)
    case 8:
      createStores(persist8)
  }
}

export const DB_NAME = 'IM'
export const DB_VERSION = 10

export const init = async () => {
  const store = reduxStore
  // TODO: support storybook store
  // const store = envMode === 'storybook' ? storyBookMockStore : reduxStore

  persist(
    DB_NAME,
    DB_VERSION,
    { upgrade },
    {
      ...persist0,
      ...persist1,
      ...persist2,
      ...persist3,
      ...persist4,
      ...persist5,
      ...persist6,
      ...persist7,
      ...persist8,
    },
    store,
    {
      afterLocalRehydrate,
      afterReduxRehydrate,
      beforeReduxRehydrate,
      req: wsReq,
      hooks,
    },
  )
}

Promise.resolve().then(init)
